import { FC, useEffect, useRef, useState } from 'react';
import { httpGet } from '../utils';
import { HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { HttpResponse } from '../../api/types/Http';
import { buildInvestigationData, entityAdTypeToTitle, getUrlByType } from './investigationDrawerBuilders';
import { useAppDrawer } from '../AppDrawer/AppDrawerProvider';
import Loader from '../Controls/Loader/Loader';
import { Icon, Icons } from '@silverfort/sf-components';
import { useHistory, useLocation } from 'react-router-dom';
import { EntityData } from './types';
import { usePricingModulesState } from '../../context/contexts/PricingModuleContext';

interface InvestigationDrawerProps {
    guid?: string;
    identifier?: string;
    adType: number;
}

const InvestigationDrawer: FC<InvestigationDrawerProps> = ({ guid, identifier, adType }) => {
    const [entityData, setEntityData] = useState<EntityData>();
    const [entityAdType, setEntityAdType] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const { updateAppDrawerProps, closeAppDrawer } = useAppDrawer();
    const history = useHistory();
    const location = useLocation();
    const firstRender = useRef(true);
    const { pricingModulesState } = usePricingModulesState();

    const onEntityClick = (guid, identifier, adType) => {
        const params = new URLSearchParams(location.search);
        params.set('investigateGuid', guid);
        params.set('investigateAdType', adType);
        params.set('investigateIdentifier', identifier);
        history.push({
            pathname: location.pathname,
            search: params.toString()
        });

        setIsLoading(true);
        getEntity(guid, identifier, adType);
    };

    const goToPrev = () => {
        history.goBack();
    };

    const getEntity = async (entityGuid, identifier, type) => {
        try {
            const url = getUrlByType(type);

            if (!url) {
                console.error('Failed to retrieve entity data - unknown AD entity type.');
                return;
            }

            const response = (await httpGet(url, {
                [HTTP_OPTIONS.REQ_OPTIONS]: { params: { entityGuid, displayName: identifier } }
            })) as unknown as HttpResponse<EntityData>;

            setEntityData(response.data);
            setEntityAdType(type);

            updateAppDrawerProps({
                headerTitle: `Manage ${entityAdTypeToTitle[type]}`,
                headerClassName: 'border-b border-[#EDEDED]',
                onClose: () => {
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.delete('investigateAdType');
                    searchParams.delete('investigateGuid');
                    searchParams.delete('investigateIdentifier');

                    history.replace({
                        pathname: location.pathname,
                        search: searchParams.toString()
                    });
                }
            });
        } catch (e) {
            console.error('Failed to retrieve investigation data for guid ' + entityGuid);
        } finally {
            if (firstRender.current) {
                firstRender.current = false;
            }
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const currentParams = new URLSearchParams(location.search);
        const nextParams = new URLSearchParams();

        guid && nextParams.set('investigateGuid', guid);
        adType && nextParams.set('investigateAdType', adType.toString());
        identifier && nextParams.set('investigateIdentifier', identifier);

        // Only push if the params actually changed
        if (nextParams.toString() !== currentParams.toString()) {
            history.push({
                pathname: location.pathname,
                search: nextParams.toString()
            });
        }

        if (firstRender.current) {
            setIsLoading(true);
            getEntity(guid, identifier, adType);
        }
    }, [guid, adType, identifier]);

    useEffect(() => {
        if (firstRender.current) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const type = params.get('investigateAdType');

        if (!type) {
            closeAppDrawer();
        } else {
            setIsLoading(true);
            getEntity(params.get('investigateGuid'), params.get('investigateIdentifier'), parseInt(type));
        }
    }, [location]);

    if (isLoading || !entityData) {
        return (
            <div className="h-full w-[800px] 2xl:w-[1100px] flex items-center">
                <Loader darkTheme={false} extraStyles={{}} />
            </div>
        );
    }

    const isFirstEntity = entityData?.details?.guid === guid;

    return (
        <div className="flex flex-col justify-between">
            <div className="w-[800px] 2xl:w-[1100px] h-fit font-sf text-sf-gray-800 flex flex-col justify-between">
                {buildInvestigationData(entityData, entityAdType, onEntityClick, pricingModulesState)}
            </div>

            {!isFirstEntity && (
                <div className="flex items-center px-4 py-2">
                    <Icon
                        onClick={goToPrev}
                        size={24}
                        icon={Icons.ArrowBack}
                        className="text-sf-gray-400 hover:text-sf-gray-500 transition ease-in hover:cursor-pointer"
                    />
                </div>
            )}
        </div>
    );
};

export default InvestigationDrawer;
